<template>
  <div class="staff-form">
    <b-card>
      <template #header>
        <h2 class="mb-0">{{ isEdit ? $t('staff.edit_title') : $t('staff.create_title') }}</h2>
      </template>
      <b-form @submit.prevent="handleSubmit" :validated="validated" novalidate>
        <!-- Form-level error message -->
        <b-alert v-if="formLevelError" variant="danger" dismissible @dismissed="formLevelError = ''" class="mb-4">
          {{ formLevelError }}
        </b-alert>
        
        <b-form-group
          :label="$t('staff.form.name_label')"
          label-for="full_name"
          :state="formErrors.has('full_name') ? false : null"
          :invalid-feedback="formErrors.first('full_name')"
        >
          <b-form-input
            id="full_name"
            v-model="formFields.full_name"
            :state="formErrors.has('full_name') ? false : null"
            required
            @input="removeError('full_name')"
          ></b-form-input>
        </b-form-group>
        
        <b-form-group
          :label="$t('staff.form.email_label')"
          label-for="email"
          :state="formErrors.has('email') ? false : null"
          :invalid-feedback="formErrors.first('email')"
        >
          <b-form-input
            id="email"
            v-model="formFields.email"
            type="email"
            :state="formErrors.has('email') ? false : null"
            required
            @input="removeError('email')"
          ></b-form-input>
        </b-form-group>
        
        <b-form-group
          :label="$t('staff.form.phone_label')"
          label-for="phone"
          :state="formErrors.has('phone') ? false : null"
          :invalid-feedback="formErrors.first('phone')"
        >
          <b-form-input
            id="phone"
            v-model="formFields.phone"
            :state="formErrors.has('phone') ? false : null"
            @input="removeError('phone')"
          ></b-form-input>
        </b-form-group>
        
        <b-form-group
          :label="$t('staff.form.department_label')"
          label-for="department_id"
          :state="formErrors.has('department_id') ? false : null"
          :invalid-feedback="formErrors.first('department_id')"
        >
          <b-form-select
            id="department_id"
            v-model="formFields.department_id"
            :state="formErrors.has('department_id') ? false : null"
            @change="removeError('department_id')"
          >
            <b-form-select-option value="" disabled>{{ $t('staff.form.select_department') }}</b-form-select-option>
            <b-form-select-option 
              v-for="department in departments" 
              :key="department.id" 
              :value="department.id"
            >
              {{ department.name }}
            </b-form-select-option>
          </b-form-select>
        </b-form-group>
        
        <b-form-group
          :label="$t('staff.form.role_label')"
          label-for="role"
          :state="formErrors.has('role') ? false : null"
          :invalid-feedback="formErrors.first('role')"
        >
          <b-form-select
            id="role"
            v-model="formFields.role"
            :state="formErrors.has('role') ? false : null"
            required
            @change="removeError('role')"
          >
            <b-form-select-option value="" disabled>{{ $t('staff.form.select_role') }}</b-form-select-option>
            <b-form-select-option 
              v-for="role in staffRoles" 
              :key="role.value" 
              :value="role.value"
            >
              {{ role.label }}
            </b-form-select-option>
          </b-form-select>
        </b-form-group>
        
        
        <div class="d-flex gap-2 mt-4">
          <b-button type="submit" variant="primary" :disabled="loader">
            <b-spinner small v-if="loader" class="mr-1"></b-spinner>
            {{ loader ? $t('common.saving') : $t('common.save') }}
          </b-button>
          <b-button variant="secondary" @click="cancel">{{ $t('common.cancel') }}</b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import NotifyMixin from "@/Util/NotifyMixin";
import { mapGetters } from "vuex";
import Error from "@/Util/Error";
const FORM_STATE = {
    full_name: null,
    email: null,
    phone: null,
    department_id: null,
    role: null
};
export default {
  name: "StaffForm",
  mixins: [ NotifyMixin ],
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      validated: false,
      formErrors: new Error({}),
      formLevelError: "",
      departments: [],
      staffRoles: [
        { value: 'housekeeping', label: 'Housekeeping' },
        { value: 'front_desk', label: 'Front Desk' },
        { value: 'maintenance', label: 'Maintenance' },
        { value: 'kitchen', label: 'Kitchen' },
        { value: 'security', label: 'Security' },
        { value: 'concierge', label: 'Concierge' },
        { value: 'management', label: 'Management' },
        { value: 'laundry', label: 'Laundry' },
        { value: 'room_service', label: 'Room Service' }
      ],
      formFields: { ...FORM_STATE },
    };
  },
  computed: {
    isEdit() {
      return !!this.id;
    },
    ...mapGetters(['loader'])
  },
  methods: {
    async fetchDepartments() {
      try {
        const response = await request({
          method: "get",
          url: "/departments",
        });
        this.departments = response.data || [];
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    },
    async fetchStaff() {
      try {
        const response = await request({
          method: "get",
          url: `/staff/${this.id}`,
        });
        this.formFields = {
          ...this.formFields,
          ...response.data,
        };

        // Set role from the roles relationship
        if (response.data.roles && response.data.roles.length > 0) {
          this.formFields.role = response.data.roles[0].name;
        }
        
      } catch (error) {
        this.formLevelError = error.response?.data?.message || this.$t("staff.fetch_error");
        this.notifyError(this.formLevelError);
        setTimeout(() => {
          this.$router.push({name:"AdminStaff"});
        }, 1000);
      }
    },
    async handleSubmit() {
      try {
        this.validated = true;
        this.formLevelError = "";
        const url = this.isEdit ? `/staff/${this.id}` : "/staff";
        const method = this.isEdit ? "put" : "post";
        const submitData = { ...this.formFields };
        await request({
          method,
          url,
          data: submitData,
        });

        const successMessage = this.isEdit 
          ? this.$t("staff.updated_successfully") 
          : this.$t("staff.created_successfully");

        this.notifySuccessWithMsg(successMessage);
        setTimeout(() => {
          this.$router.push({name:"AdminStaff"});
        }, 1000);
      } catch (err) {
        if(err?.data?.errors && err.status == 422){
          Object.assign(this.formErrors, new Error(err.data.errors));
          this.validated = true;
        }else{
          this.error = err.data?.message || this.$t("staff.save_error");
          this.notifyError(this.error);
        }
      }
    },
    cancel() {
      this.$router.push("/admin/staff");
    },
    async removeError(target) {
        if (typeof target === 'object') {
            for (let i = 0; i < target.length; i++) {
                this.formErrors.remove(target[i]);
            }
        } else {
            this.formErrors.remove(target);
        }
    },
  },
  async mounted() {
    await this.fetchDepartments();
    if (this.isEdit) {
      await this.fetchStaff();
    }
  },
};
</script>
